import { useEffect, useMemo, useState } from 'react';
import { useScenarioStore } from '../../store/scenarioStore';
import { useStressTestStore } from '../../store/stressTestStore';
import { isKabelverteilschrank, isTrafoStation } from '../../types/network';
import { measureValueLabel } from '../../utils/planningLabels';

interface MeasureEditorProps {
  /** Abgangsleitung id or TrafoStation id. */
  elementId: string;
  kind: 'cable_reinforcement' | 'trafo_upgrade';
  /** Current cable type name / current rating in kVA. */
  current: string | number | undefined;
}

const inputCls = 'px-2 py-1.5 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100';
const primaryBtn = 'px-3 py-1.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50';

/**
 * Grid measures of the scenario for one element, stored with the scenario
 * (`measures`) and applied on copies in the planning run — the live network
 * is never changed.
 *
 * Cable: new standard type (the matching fuse is proposed at once — a bigger
 * conductor behind the old fuse changes nothing) or a fuse swap alone.
 * Trafo: new rating, and the tap changer / rONT setpoint of the station.
 */
export function MeasureEditor({ elementId, kind, current }: MeasureEditorProps) {
  const catalog = useStressTestStore(s => s.catalog);
  const loadCatalog = useStressTestStore(s => s.loadCatalog);

  useEffect(() => {
    if (!catalog) void loadCatalog();
  }, [catalog, loadCatalog]);

  return kind === 'cable_reinforcement'
    ? <CableMeasures cableId={elementId} currentType={current ? String(current) : undefined} />
    : <TrafoMeasures trafoId={elementId} currentKva={typeof current === 'number' ? current : undefined} />;
}

function Card({ title, active, children }: { title: string; active: boolean; children: React.ReactNode }) {
  return (
    <div className={`rounded-lg border ${active ? 'border-orange-400 dark:border-orange-500 bg-orange-50/60 dark:bg-orange-900/10' : 'border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-800'} p-3 space-y-2`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900 dark:text-primary-100">Maßnahme · {title}</h3>
        {active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">im Szenario</span>}
      </div>
      {children}
    </div>
  );
}

function Footer() {
  return (
    <p className="text-[10px] text-gray-500 dark:text-primary-400 leading-snug">
      Wirkt nur im Planungslauf (Vorher/Nachher); das Netzmodell bleibt unverändert.
    </p>
  );
}

// ── Cable: type + fuse ────────────────────────────────────────────────────

function useCurrentFuse(cableId: string): number | undefined {
  const stressElements = useStressTestStore(s => s.stressElements);
  return useMemo(() => {
    let rating: number | undefined;
    for (const el of stressElements) {
      if (!isTrafoStation(el) && !isKabelverteilschrank(el)) continue;
      const fuse = el.fuses?.[cableId];
      if (fuse && fuse.isInserted !== false && fuse.current > 0) {
        rating = rating == null ? fuse.current : Math.min(rating, fuse.current);
      }
    }
    return rating;
  }, [stressElements, cableId]);
}

function CableMeasures({ cableId, currentType }: { cableId: string; currentType?: string }) {
  const catalog = useStressTestStore(s => s.catalog);
  const cableMeasure = useScenarioStore(s => s.pendingMeasures.find(m => m.elementId === cableId && m.kind === 'cable_reinforcement'));
  const fuseMeasure = useScenarioStore(s => s.pendingMeasures.find(m => m.elementId === cableId && m.kind === 'fuse_upgrade'));
  const setMeasure = useScenarioStore(s => s.setMeasure);
  const removeMeasure = useScenarioStore(s => s.removeMeasure);
  const currentFuse = useCurrentFuse(cableId);

  const [typeChoice, setTypeChoice] = useState('');
  const [withFuse, setWithFuse] = useState(true);
  const [fuseChoice, setFuseChoice] = useState('');

  useEffect(() => { setTypeChoice(cableMeasure?.cableType ?? ''); }, [cableMeasure]);
  useEffect(() => { setFuseChoice(fuseMeasure?.fuseRatingA != null ? String(fuseMeasure.fuseRatingA) : ''); }, [fuseMeasure]);

  const types = catalog?.cableTypes ?? [];
  const fuses = catalog?.fuseRatingsA ?? [];
  const chosenType = types.find(t => t.name === typeChoice);
  /** Largest catalogue fuse the new conductor may carry (I_n ≤ I_z), at most its standard fuse. */
  const proposedFuse = chosenType
    ? Math.min(chosenType.fuseCurrentA, ...fuses.filter(f => f <= chosenType.maxCurrentA).slice(-1))
    : undefined;
  const fuseChangeNeeded = proposedFuse != null && (currentFuse == null || currentFuse !== proposedFuse);

  const applyType = () => {
    if (!chosenType) return;
    setMeasure({ id: cableMeasure?.id ?? crypto.randomUUID(), kind: 'cable_reinforcement', elementId: cableId, cableType: chosenType.name });
    if (withFuse && proposedFuse != null && fuseChangeNeeded) {
      setMeasure({ id: fuseMeasure?.id ?? crypto.randomUUID(), kind: 'fuse_upgrade', elementId: cableId, fuseRatingA: proposedFuse });
    }
  };
  const applyFuse = () => {
    if (!fuseChoice) return;
    setMeasure({ id: fuseMeasure?.id ?? crypto.randomUUID(), kind: 'fuse_upgrade', elementId: cableId, fuseRatingA: Number(fuseChoice) });
  };

  return (
    <Card title="Kabel verstärken / Sicherung" active={!!cableMeasure || !!fuseMeasure}>
      <p className="text-xs text-gray-600 dark:text-primary-300">
        Heute: <span className="font-medium text-gray-900 dark:text-primary-100">{currentType ?? 'unbekannter Typ'}</span>
        {' '}· Sicherung <span className="font-medium text-gray-900 dark:text-primary-100">{currentFuse != null ? `${currentFuse} A` : 'unbekannt'}</span>
      </p>

      <div className="flex gap-2">
        <select value={typeChoice} onChange={(e) => setTypeChoice(e.target.value)} className={`flex-1 ${inputCls}`} data-testid="measure-cable-type">
          <option value="">Neuer Kabeltyp…</option>
          {types.map(t => <option key={t.name} value={t.name}>{t.name} · {t.maxCurrentA} A</option>)}
        </select>
        <button type="button" onClick={applyType} disabled={!chosenType} className={primaryBtn}>
          {cableMeasure ? 'Ändern' : 'Vormerken'}
        </button>
      </div>
      {chosenType && (
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-primary-200 cursor-pointer">
          <input type="checkbox" checked={withFuse} onChange={(e) => setWithFuse(e.target.checked)} data-testid="measure-cable-fuse" />
          <span>
            Sicherung passend tauschen{proposedFuse != null ? ` (→ ${proposedFuse} A)` : ''}
            {!fuseChangeNeeded && proposedFuse != null && <span className="text-gray-400 dark:text-primary-500"> – bereits passend</span>}
          </span>
        </label>
      )}
      {cableMeasure && (
        <p className="text-xs text-gray-700 dark:text-primary-200">
          Vorgemerkt: <span className="font-medium">{measureValueLabel(cableMeasure)}</span>
          {' '}<button type="button" onClick={() => removeMeasure(cableMeasure.id)} className="text-red-600 dark:text-red-400 hover:underline">entfernen</button>
        </p>
      )}

      <div className="flex gap-2 pt-1 border-t border-dashed border-gray-200 dark:border-primary-600">
        <select value={fuseChoice} onChange={(e) => setFuseChoice(e.target.value)} className={`flex-1 ${inputCls}`} data-testid="measure-fuse-rating">
          <option value="">Nur Sicherung tauschen…</option>
          {fuses.map(f => <option key={f} value={String(f)}>{f} A{currentFuse === f ? ' (heute)' : ''}</option>)}
        </select>
        <button type="button" onClick={applyFuse} disabled={!fuseChoice} className={primaryBtn}>
          {fuseMeasure ? 'Ändern' : 'Vormerken'}
        </button>
      </div>
      {fuseMeasure && (
        <p className="text-xs text-gray-700 dark:text-primary-200">
          Vorgemerkt: <span className="font-medium">Sicherung {measureValueLabel(fuseMeasure)}</span>
          {' '}<button type="button" onClick={() => removeMeasure(fuseMeasure.id)} className="text-red-600 dark:text-red-400 hover:underline">entfernen</button>
        </p>
      )}
      <p className="text-[10px] text-gray-500 dark:text-primary-400 leading-snug">
        Die Sicherung begrenzt den Abgang oft vor dem Leiter: die Auswertung zeigt je Kabel die Sicherungsauslastung, ein Kabeltausch ohne passende Sicherung bringt nichts.
      </p>
      <Footer />
    </Card>
  );
}

// ── Trafo: rating + tap / rONT ────────────────────────────────────────────

function TrafoMeasures({ trafoId, currentKva }: { trafoId: string; currentKva?: number }) {
  const catalog = useStressTestStore(s => s.catalog);
  const run = useStressTestStore(s => s.run);
  const ratingMeasure = useScenarioStore(s => s.pendingMeasures.find(m => m.elementId === trafoId && m.kind === 'trafo_upgrade'));
  const tapMeasure = useScenarioStore(s => s.pendingMeasures.find(m => m.elementId === trafoId && m.kind === 'trafo_tap'));
  const setMeasure = useScenarioStore(s => s.setMeasure);
  const removeMeasure = useScenarioStore(s => s.removeMeasure);

  const [kvaChoice, setKvaChoice] = useState('');
  const [setpoint, setSetpoint] = useState('');
  useEffect(() => { setKvaChoice(ratingMeasure?.ratedKva != null ? String(ratingMeasure.ratedKva) : ''); }, [ratingMeasure]);
  useEffect(() => { setSetpoint(tapMeasure?.voltageSetpointPu != null ? String(tapMeasure.voltageSetpointPu) : ''); }, [tapMeasure]);

  const steps = catalog?.tapStepsPercent ?? [-5, -2.5, 2.5, 5];
  const archivedBusbar = run?.busbarSetpoints?.baseline?.[trafoId];

  const applyRating = () => {
    if (!kvaChoice) return;
    setMeasure({ id: ratingMeasure?.id ?? crypto.randomUUID(), kind: 'trafo_upgrade', elementId: trafoId, ratedKva: Number(kvaChoice) });
  };
  const applyStep = (step: number) => {
    setMeasure({ id: tapMeasure?.id ?? crypto.randomUUID(), kind: 'trafo_tap', elementId: trafoId, tapStepPercent: step });
  };
  const applySetpoint = () => {
    const v = Number(setpoint.replace(',', '.'));
    if (!Number.isFinite(v)) return;
    const min = catalog?.setpointMinPu ?? 0.95;
    const max = catalog?.setpointMaxPu ?? 1.05;
    setMeasure({ id: tapMeasure?.id ?? crypto.randomUUID(), kind: 'trafo_tap', elementId: trafoId, voltageSetpointPu: Math.min(max, Math.max(min, v)) });
  };
  const activeStep = tapMeasure?.voltageSetpointPu == null ? tapMeasure?.tapStepPercent : undefined;

  return (
    <Card title="Trafo tauschen / Stufenschalter" active={!!ratingMeasure || !!tapMeasure}>
      <p className="text-xs text-gray-600 dark:text-primary-300">
        Heute: <span className="font-medium text-gray-900 dark:text-primary-100">{currentKva ? `${currentKva} kVA` : 'unbekannt'}</span>
        {archivedBusbar != null && (
          <> · Sammelschiene im Archiv-Frame <span className="font-medium text-gray-900 dark:text-primary-100">{archivedBusbar.toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} pu</span></>
        )}
      </p>

      <div className="flex gap-2">
        <select value={kvaChoice} onChange={(e) => setKvaChoice(e.target.value)} className={`flex-1 ${inputCls}`} data-testid="measure-trafo-kva">
          <option value="">Neue Nennleistung…</option>
          {(catalog?.transformerSizesKva ?? []).map(k => <option key={k} value={String(k)}>{k} kVA</option>)}
        </select>
        <button type="button" onClick={applyRating} disabled={!kvaChoice} className={primaryBtn}>{ratingMeasure ? 'Ändern' : 'Vormerken'}</button>
      </div>
      {ratingMeasure && (
        <p className="text-xs text-gray-700 dark:text-primary-200">
          Vorgemerkt: <span className="font-medium">{measureValueLabel(ratingMeasure)}</span>
          {' '}<button type="button" onClick={() => removeMeasure(ratingMeasure.id)} className="text-red-600 dark:text-red-400 hover:underline">entfernen</button>
        </p>
      )}

      <div className="pt-1 border-t border-dashed border-gray-200 dark:border-primary-600 space-y-1.5">
        <p className="text-[11px] font-medium text-gray-700 dark:text-primary-200">Stufenschalter (auf die Sammelschienenspannung des Lastfalls)</p>
        <div className="flex gap-1" data-testid="measure-tap-steps">
          {steps.map(step => (
            <button
              key={step}
              type="button"
              onClick={() => applyStep(step)}
              className={`flex-1 px-1 py-1 text-xs rounded border ${activeStep === step ? 'bg-orange-500 text-white border-orange-500' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 border-gray-300 dark:border-primary-500 hover:bg-gray-50 dark:hover:bg-primary-700'}`}
            >
              {step > 0 ? '+' : ''}{step.toLocaleString('de-DE')} %
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-[11px] text-gray-600 dark:text-primary-300 whitespace-nowrap">rONT-Sollwert</span>
          <input
            type="text"
            inputMode="decimal"
            value={setpoint}
            onChange={(e) => setSetpoint(e.target.value)}
            placeholder={`${(catalog?.setpointMinPu ?? 0.95).toLocaleString('de-DE')}–${(catalog?.setpointMaxPu ?? 1.05).toLocaleString('de-DE')} pu`}
            className={`flex-1 min-w-0 ${inputCls}`}
            data-testid="measure-setpoint"
          />
          <button type="button" onClick={applySetpoint} disabled={!setpoint} className={primaryBtn}>Halten</button>
        </div>
        {tapMeasure && (
          <p className="text-xs text-gray-700 dark:text-primary-200">
            Vorgemerkt: <span className="font-medium">{measureValueLabel(tapMeasure)}</span>
            {' '}<button type="button" onClick={() => removeMeasure(tapMeasure.id)} className="text-red-600 dark:text-red-400 hover:underline">entfernen</button>
          </p>
        )}
        <p className="text-[10px] text-gray-500 dark:text-primary-400 leading-snug">
          Der Pool hält die NS-Sammelschiene dieser Station im Lauf auf dem Sollwert (je Station, auch im Verbund) — wie ein rONT im geschlossenen Regelkreis.
        </p>
      </div>
      <Footer />
    </Card>
  );
}
